import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface OhmsLawDiagramProps {
    voltage: number;
    resistance: number;
    current: number; // The calculated/given current
    solveFor: 'current' | 'voltage' | 'resistance';
}

// Standardized diagram dimensions
const DIAGRAM_WIDTH = 450;
const DIAGRAM_HEIGHT = 300;

export const OhmsLawDiagram: React.FC<OhmsLawDiagramProps> = ({
    voltage,
    resistance,
    current,
    solveFor
}) => {
    // Layout constants
    const width = DIAGRAM_WIDTH;
    const height = DIAGRAM_HEIGHT;
    const padding = 60;

    const leftX = padding + 30;
    const rightX = width - padding - 30;
    const topY = padding;
    const bottomY = height - padding;

    const vLabel = solveFor === 'voltage' ? 'V = ?' : undefined;
    const rLabel = solveFor === 'resistance' ? 'R = ?' : undefined;
    const iLabel = solveFor === 'current' ? 'I = ?' : `I = ${current} A`;

    const highlightColor = "#2563eb"; // Blue for unknown

    // Circuit follows a Clockwise loop roughly:
    // Top: L->R
    // Right: T->B
    // Bottom: R->L
    // Left: B->T

    // Default Renderer Normal is "Right" (Inside the loop).
    // We want labels OUTSIDE.
    // So we need 'left' side for all?
    // Let's trace:
    // Top (L->R): Right=Down(In). Left=Up(Out). -> 'left'
    // Right (T->B): Right=Left(In). Left=Right(Out). -> 'left'
    // Bottom (R->L): Right=Up(In). Left=Down(Out). -> 'left'
    // Left (B->T): Right=Right(In). Left=Left(Out). -> 'left'

    // So for a CW loop, 'left' pushes everything outside.

    const elements: CircuitElement[] = [
        // Top Wire
        {
            type: 'wire',
            from: { x: leftX, y: topY },
            to: { x: rightX, y: topY },
            labelSide: 'left' // Out
        },

        // Right Resistor (Top -> Bottom) - Reversing direction from previous iteration to match loop flow
        {
            type: 'resistor',
            from: { x: rightX, y: topY },
            to: { x: rightX, y: bottomY },
            value: resistance,
            label: rLabel,
            labelSide: 'left' // Out
        },

        // Bottom Wire / Current Arrow (Right -> Left)
        {
            type: 'currentArrow',
            from: { x: rightX, y: bottomY },
            to: { x: leftX, y: bottomY },
            label: iLabel,
            labelColor: solveFor === 'current' ? highlightColor : undefined,
            labelOffset: 30,
            labelSide: 'left' // Out
        },

        // Left Voltage Source (Bottom -> Top)
        {
            type: 'voltageSource',
            from: { x: leftX, y: bottomY },
            to: { x: leftX, y: topY },
            value: voltage,
            label: vLabel,
            labelSide: 'left' // Out
        }
    ];

    return <CircuitRenderer width={width} height={height} elements={elements} />;
};
